import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

/* ───────────────── FETCH BANNERS ───────────────── */
export const fetchBanners = createAsyncThunk(
  "banner/fetchBanners",
  async ({ page = 1, limit = 10, search = "" }, thunkAPI) => {
    try {

      const response = await axios.get(
        `${BASE_URL}/api/banners/get_banner?page=${page}&limit=${limit}&search=${search}`
      );

      return response.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch banners"
      );

    }
  }
);

/* ───────────────── CREATE BANNER ───────────────── */
export const createBanner = createAsyncThunk(
  "banner/createBanner",
  async (formData, thunkAPI) => {
    try {

      const response = await axios.post(
        `${BASE_URL}/api/banners/add_banner`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to create banner"
      );

    }
  }
);

/* ───────────────── UPDATE BANNER ───────────────── */
export const updateBanner = createAsyncThunk(
  "banner/updateBanner",
  async ({ id, formData }, thunkAPI) => {
    try {

      const response = await axios.put(
        `${BASE_URL}/api/banners/update_banner/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to update banner"
      );

    }
  }
);

/* ───────────────── DELETE BANNER ───────────────── */
export const deleteBanner = createAsyncThunk(
  "banner/deleteBanner",
  async (id, thunkAPI) => {
    try {

      await axios.delete(
        `${BASE_URL}/api/banners/delete_banner/${id}`
      );

      return id;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to delete banner"
      );

    }
  }
);

/* ───────────────── TOGGLE STATUS ───────────────── */
export const toggleBannerStatus = createAsyncThunk(
  "banner/toggleBannerStatus",
  async (id, thunkAPI) => {
    try {

      const response = await axios.put(
        `${BASE_URL}/api/banners/toggle_banner/${id}`
      );

      return response.data.data;

    } catch (error) {

      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to update status"
      );

    }
  }
);

/* ───────────────── SLICE ───────────────── */
const bannerSlice = createSlice({

  name: "banner",

  initialState: {

    banners: [],
    pagination: {},
    loading: false,
    error: null,

  },

  reducers: {},

  extraReducers: (builder) => {

    builder

      /* FETCH */
      .addCase(fetchBanners.pending, (state) => {

        state.loading = true;
        state.error = null;

      })

      .addCase(fetchBanners.fulfilled, (state, action) => {

        state.loading = false;

        state.banners =
          action.payload.data || [];

        state.pagination =
          action.payload.pagination || {};

      })

      .addCase(fetchBanners.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* CREATE */
      .addCase(createBanner.pending, (state) => {

        state.loading = true;

      })

      .addCase(createBanner.fulfilled, (state, action) => {

        state.loading = false;

        state.banners.unshift(action.payload);

      })

      .addCase(createBanner.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* UPDATE */
      .addCase(updateBanner.pending, (state) => {

        state.loading = true;

      })

      .addCase(updateBanner.fulfilled, (state, action) => {

        state.loading = false;

        const index = state.banners.findIndex(
          (item) => item._id === action.payload._id
        );

        if (index !== -1) {

          state.banners[index] =
            action.payload;

        }

      })

      .addCase(updateBanner.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* DELETE */
      .addCase(deleteBanner.pending, (state) => {

        state.loading = true;

      })

      .addCase(deleteBanner.fulfilled, (state, action) => {

        state.loading = false;

        state.banners =
          state.banners.filter(
            (item) => item._id !== action.payload
          );

      })

      .addCase(deleteBanner.rejected, (state, action) => {

        state.loading = false;
        state.error = action.payload;

      })

      /* TOGGLE STATUS */
      .addCase(toggleBannerStatus.fulfilled, (state, action) => {

        const index = state.banners.findIndex(
          (item) => item._id === action.payload._id
        );

        if (index !== -1) {

          state.banners[index] =
            action.payload;

        }

      });

  },

});

export default bannerSlice.reducer;